import { Link } from "react-router-dom";

import LandingNavBar from "../../components/LandingNavBar/LandingNavBar";

import "./LandingPage.scss";

const LandingPage = () => {
  return (
    <div className="landing-page">
      {/* =========================================================
          SHARED REACT LANDING NAVBAR
      ========================================================== */}

      <LandingNavBar />

      {/* =========================================================
          MAIN LANDING PAGE
      ========================================================== */}

      <main className="landing-page__main">
        {/* =======================================================
            HERO
        ======================================================== */}

        <section className="landing-hero">
          <div className="landing-container landing-hero__inner">
            <p className="landing-eyebrow">About Postly</p>

            <h1>
              A Clearer System For{" "}
              <span>Task Management And Project Planning</span>
            </h1>

            <p className="landing-hero__lead">
              Postly is a focused workspace for turning ideas into organized
              action. Plan projects, organize tasks, capture notes, establish
              priorities, and build repeatable processes, checklists, and
              routines without the administrative overhead of traditional
              project management software.
            </p>

            <div className="landing-hero__actions">
              <Link
                className="landing-button landing-button--primary"
                to="/auth/sign-up"
              >
                Create Your Free Account
              </Link>

              <a
                className="landing-button landing-button--secondary"
                href="/features"
              >
                Explore Features
              </a>
            </div>

            <div className="landing-workflow" aria-label="Postly workflow">
              <div className="landing-workflow__item landing-workflow__item--yellow">
                <span className="landing-workflow__number">01</span>

                <span className="landing-workflow__label">Capture</span>

                <span className="landing-workflow__description">
                  Ideas and context
                </span>
              </div>

              <div className="landing-workflow__arrow" aria-hidden="true">
                →
              </div>

              <div className="landing-workflow__item landing-workflow__item--blue">
                <span className="landing-workflow__number">02</span>

                <span className="landing-workflow__label">Organize</span>

                <span className="landing-workflow__description">
                  Projects and tasks
                </span>
              </div>

              <div className="landing-workflow__arrow" aria-hidden="true">
                →
              </div>

              <div className="landing-workflow__item landing-workflow__item--yellow">
                <span className="landing-workflow__number">03</span>

                <span className="landing-workflow__label">Prioritize</span>

                <span className="landing-workflow__description">
                  What matters next
                </span>
              </div>

              <div className="landing-workflow__arrow" aria-hidden="true">
                →
              </div>

              <div className="landing-workflow__item landing-workflow__item--green">
                <span className="landing-workflow__number">04</span>

                <span className="landing-workflow__label">Execute</span>

                <span className="landing-workflow__description">
                  Move work forward
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* =======================================================
            PROBLEM
        ======================================================== */}

        <section className="landing-section landing-section--bordered">
          <div className="landing-container landing-section-grid">
            <div className="landing-section-grid__heading">
              <p className="landing-eyebrow">The problem</p>

              <h2>Work gets complicated before it gets organized.</h2>
            </div>

            <div className="landing-section-grid__content landing-prose">
              <p>
                Most work does not begin inside a perfectly structured project
                plan. It begins as an idea.
              </p>

              <p>
                A note from a meeting. A deadline you cannot forget. A feature
                you want to build. An assignment due next week. A client who
                needs a follow-up. A piece of content you want to publish. A
                recurring responsibility that keeps returning.
              </p>

              <p>
                Individually, none of these things are particularly complicated.
                The problem is what happens when they accumulate.
              </p>

              <p>
                Ideas end up in one notes app. Tasks live somewhere else.
                Deadlines are remembered mentally. Project details sit inside
                documents. Recurring responsibilities become reminders you
                recreate every week. Processes stay in your head until something
                gets missed.
              </p>

              <p className="landing-prose__emphasis">
                The problem is often not a lack of effort. It is a lack of
                structure.
              </p>

              <p>
                Postly was built around the belief that good execution begins by
                turning scattered information into a system you can understand
                and act on.
              </p>
            </div>
          </div>
        </section>

        {/* =======================================================
            EXECUTION BUILDING BLOCKS
        ======================================================== */}

        <section className="landing-section landing-section--soft">
          <div className="landing-container">
            <div className="landing-section-heading landing-section-heading--center">
              <p className="landing-eyebrow">Built around execution</p>

              <h2>The essential building blocks of getting work done.</h2>

              <p>
                Postly gives serious work enough structure to stay organized
                without turning the system itself into another project.
              </p>
            </div>

            <div className="landing-principles-grid">
              <article className="landing-principle-card landing-principle-card--yellow">
                <span className="landing-principle-card__index">01</span>

                <h3>Projects provide structure.</h3>

                <p>
                  Turn goals, assignments, client work, launches, and ideas into
                  organized bodies of work with a clear purpose.
                </p>
              </article>

              <article className="landing-principle-card landing-principle-card--blue">
                <span className="landing-principle-card__index">02</span>

                <h3>Tasks define action.</h3>

                <p>
                  Break larger goals into concrete next steps so progress
                  becomes visible, manageable, and easier to maintain.
                </p>
              </article>

              <article className="landing-principle-card landing-principle-card--green">
                <span className="landing-principle-card__index">03</span>

                <h3>Priorities establish focus.</h3>

                <p>
                  Separate what requires attention now from what can wait and
                  keep the most important work visible.
                </p>
              </article>

              <article className="landing-principle-card landing-principle-card--yellow">
                <span className="landing-principle-card__index">04</span>

                <h3>Notes preserve context.</h3>

                <p>
                  Keep research, ideas, decisions, meeting takeaways, and
                  supporting information closer to the work they belong to.
                </p>
              </article>

              <article className="landing-principle-card landing-principle-card--blue">
                <span className="landing-principle-card__index">05</span>

                <h3>Checklists improve consistency.</h3>

                <p>
                  Capture the important steps behind work that needs to be
                  completed carefully and repeatedly.
                </p>
              </article>

              <article className="landing-principle-card landing-principle-card--green">
                <span className="landing-principle-card__index">06</span>

                <h3>Routines make repetition easier.</h3>

                <p>
                  Create structure around responsibilities that return every
                  day, week, month, project, or client engagement.
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* =======================================================
            FROM THINKING TO DOING
        ======================================================== */}

        <section className="landing-section">
          <div className="landing-container landing-section-grid">
            <div className="landing-section-grid__heading">
              <p className="landing-eyebrow">From thinking to doing</p>

              <h2>Give every idea a path toward execution.</h2>
            </div>

            <div className="landing-section-grid__content landing-prose">
              <p>
                A useful productivity system should help you move from thinking
                to doing.
              </p>

              <p>
                Postly gives ideas a place to go. A new goal can become a
                project. A project can be broken into tasks. Tasks can be
                prioritized so the next action becomes clear.
              </p>

              <p>
                Supporting information can stay connected through notes. Work
                that needs to happen correctly every time can become a
                checklist. Responsibilities that repeat can become routines and
                reusable processes.
              </p>

              <p>
                Instead of treating productivity as an endless collection of
                individual to-dos, Postly helps you create structure around how
                your work actually happens.
              </p>

              <blockquote>
                A task manager tells you what you wrote down. A useful execution
                system helps you organize how work moves forward.
              </blockquote>
            </div>
          </div>
        </section>

        {/* =======================================================
            PROJECT PLANNING
        ======================================================== */}

        <section className="landing-section landing-section--dark">
          <div className="landing-container">
            <div className="landing-feature-split">
              <div>
                <p className="landing-eyebrow landing-eyebrow--light">
                  Project planning
                </p>

                <h2>Project structure without project management overhead.</h2>
              </div>

              <div className="landing-feature-split__body">
                <p>
                  Not every project needs a complicated project management
                  system.
                </p>

                <p>
                  Sometimes you need to define the outcome, identify the work,
                  establish priorities, keep the relevant notes nearby, and
                  start executing.
                </p>

                <p>
                  Postly is designed for that middle ground. It gives you more
                  structure than a basic to-do list without forcing you into the
                  complexity of enterprise project management software.
                </p>

                <p>
                  Use Postly as a project planner for product launches, client
                  engagements, academic work, research projects, content
                  campaigns, personal goals, or nearly any body of work that
                  needs to move from idea to completion.
                </p>

                <p className="landing-feature-split__statement">
                  The objective is not to create the most elaborate project
                  plan. The objective is to create enough structure that
                  progress becomes easier.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* =======================================================
            TASK MANAGEMENT
        ======================================================== */}

        <section className="landing-section">
          <div className="landing-container landing-section-grid">
            <div className="landing-section-grid__heading">
              <p className="landing-eyebrow">Task management</p>

              <h2>A long task list is not the same thing as a plan.</h2>
            </div>

            <div className="landing-section-grid__content landing-prose">
              <p>
                When everything appears equally important, a task manager can
                become another place where unfinished work accumulates.
              </p>

              <p>
                Postly is designed to help turn collections of tasks into
                clearer next steps.
              </p>

              <p>
                Tasks live within the projects they support, giving individual
                actions context instead of leaving them isolated inside an
                endless to-do list.
              </p>

              <p>
                That structure helps separate what is urgent from what can wait,
                what belongs to one project from another, and what requires
                attention now from what simply needs to be remembered.
              </p>

              <p className="landing-prose__emphasis">
                Open your workspace and understand where to focus.
              </p>
            </div>
          </div>
        </section>

        {/* =======================================================
            NOTES
        ======================================================== */}

        <section className="landing-section landing-section--soft">
          <div className="landing-container landing-section-grid">
            <div className="landing-section-grid__heading">
              <p className="landing-eyebrow">Notes and context</p>

              <h2>Keep the thinking closer to the work.</h2>
            </div>

            <div className="landing-section-grid__content landing-prose">
              <p>
                Execution depends on more than tasks. Projects accumulate
                context.
              </p>

              <p>
                Meeting takeaways. Customer observations. Research. Questions.
                Decisions. Draft ideas. Instructions. References. Things you
                need to remember later.
              </p>

              <p>
                When that information lives far away from the work itself, you
                spend more time reconstructing context every time you return to
                a project.
              </p>

              <p>
                Postly brings notes and execution closer together so the
                information surrounding your work can stay organized alongside
                the tasks and projects it supports.
              </p>

              <p className="landing-prose__emphasis">
                You should not have to remember everything in order to stay
                organized.
              </p>
            </div>
          </div>
        </section>

        {/* =======================================================
            REPEATABLE WORK
        ======================================================== */}

        <section className="landing-section">
          <div className="landing-container">
            <div className="landing-section-heading">
              <p className="landing-eyebrow">Repeatable work</p>

              <h2>Turn repeated responsibilities into systems.</h2>

              <p>
                Some of the most important work is not a one-time task. It
                happens again and again.
              </p>
            </div>

            <div className="landing-repeat-grid">
              <article className="landing-repeat-card landing-repeat-card--yellow">
                <p className="landing-repeat-card__audience">Founder</p>

                <h3>Launch a feature.</h3>

                <p>
                  Capture requirements, product tasks, testing steps, launch
                  preparation, customer communication, and follow-up.
                </p>
              </article>

              <article className="landing-repeat-card landing-repeat-card--blue">
                <p className="landing-repeat-card__audience">Consultant</p>

                <h3>Onboard a client.</h3>

                <p>
                  Organize preparation, discovery, deliverables, meeting notes,
                  reviews, and recurring client responsibilities.
                </p>
              </article>

              <article className="landing-repeat-card landing-repeat-card--green">
                <p className="landing-repeat-card__audience">Student</p>

                <h3>Prepare for an exam.</h3>

                <p>
                  Break study goals into topics, assignments, research, revision
                  sessions, and recurring study routines.
                </p>
              </article>

              <article className="landing-repeat-card landing-repeat-card--yellow">
                <p className="landing-repeat-card__audience">Creator</p>

                <h3>Publish content.</h3>

                <p>
                  Move from idea to research, draft, production, editing,
                  publishing, and post-publication follow-up.
                </p>
              </article>
            </div>

            <div className="landing-repeat-conclusion">
              <p>
                Over time, repeated work should not require you to solve the
                same organizational problem from scratch.
              </p>

              <strong>Build the system once. Improve it as you learn.</strong>
            </div>
          </div>
        </section>

        {/* =======================================================
            USE CASES
        ======================================================== */}

        <section className="landing-section landing-section--soft">
          <div className="landing-container">
            <div className="landing-section-heading landing-section-heading--center">
              <p className="landing-eyebrow">Who Postly is for</p>

              <h2>One workspace. Different ways of working.</h2>

              <p>
                Postly is designed around the underlying structure of work, not
                a single profession or industry.
              </p>
            </div>

            <div className="landing-audience-grid">
              <a
                className="landing-audience-card landing-audience-card--yellow"
                href="/solo-founders"
              >
                <div>
                  <p className="landing-audience-card__label">
                    01 / Solo Founders
                  </p>

                  <h3>
                    Keep the company moving without becoming your own project
                    administrator.
                  </h3>

                  <p>
                    Organize startup ideas, product development, customer
                    feedback, launch checklists, investor follow-ups,
                    operations, and weekly priorities.
                  </p>
                </div>

                <span className="landing-audience-card__link">
                  Explore Solo Founders →
                </span>
              </a>

              <a
                className="landing-audience-card landing-audience-card--blue"
                href="/service-professionals"
              >
                <div>
                  <p className="landing-audience-card__label">
                    02 / Service Professionals
                  </p>

                  <h3>
                    Bring client work, deadlines, notes, and follow-ups into a
                    clearer operating rhythm.
                  </h3>

                  <p>
                    Manage client projects, meeting notes, deliverables,
                    onboarding checklists, deadlines, and recurring reviews.
                  </p>
                </div>

                <span className="landing-audience-card__link">
                  Explore Service Professionals →
                </span>
              </a>

              <a
                className="landing-audience-card landing-audience-card--green"
                href="/students"
              >
                <div>
                  <p className="landing-audience-card__label">03 / Students</p>

                  <h3>
                    Turn classes, assignments, exams, and deadlines into
                    manageable plans.
                  </h3>

                  <p>
                    Break large assignments into smaller tasks, organize
                    coursework, plan study routines, and track academic
                    responsibilities.
                  </p>
                </div>

                <span className="landing-audience-card__link">
                  Explore Students →
                </span>
              </a>

              <a
                className="landing-audience-card landing-audience-card--yellow"
                href="/creators"
              >
                <div>
                  <p className="landing-audience-card__label">04 / Creators</p>

                  <h3>
                    Give creative ideas enough structure to become finished
                    work.
                  </h3>

                  <p>
                    Organize content ideas, scripts, production tasks,
                    collaborations, publishing schedules, and repeatable
                    creative workflows.
                  </p>
                </div>

                <span className="landing-audience-card__link">
                  Explore Creators →
                </span>
              </a>
            </div>

            <div className="landing-center-link">
              <a href="/use-cases">View all Postly use cases →</a>
            </div>
          </div>
        </section>

        {/* =======================================================
            PRODUCT PHILOSOPHY
        ======================================================== */}

        <section className="landing-section">
          <div className="landing-container landing-section-grid">
            <div className="landing-section-grid__heading">
              <p className="landing-eyebrow">Product philosophy</p>

              <h2>Less configuration. More execution.</h2>
            </div>

            <div className="landing-section-grid__content landing-prose">
              <p>Productivity software can become surprisingly unproductive.</p>

              <p>
                A new tool promises organization, so you spend hours designing
                dashboards, configuring statuses, creating fields, choosing
                views, building automations, and maintaining the system that was
                supposed to reduce your workload.
              </p>

              <p>Postly is built around restraint.</p>

              <p>
                The product should provide enough structure to make work clearer
                without making the system itself another project to manage.
              </p>

              <div className="landing-keyword-stack">
                <span>Projects</span>
                <span>Tasks</span>
                <span>Priorities</span>
                <span>Notes</span>
                <span>Processes</span>
                <span>Checklists</span>
                <span>Routines</span>
              </div>

              <p className="landing-prose__emphasis">
                A clear system does not need to model every possible way an
                organization might operate. It needs to help you understand your
                work and move it forward.
              </p>
            </div>
          </div>
        </section>

        {/* =======================================================
            POSITIONING
        ======================================================== */}

        <section className="landing-section landing-section--dark">
          <div className="landing-container">
            <div className="landing-positioning">
              <p className="landing-eyebrow landing-eyebrow--light">
                Designed for the people doing the work
              </p>

              <h2>
                More structure than a notebook.
                <br />
                Less complexity than enterprise software.
              </h2>

              <p>
                Postly is built for people who carry meaningful responsibility
                themselves: people building something, serving clients, studying
                toward something, creating consistently, or managing work that
                matters enough to require structure.
              </p>

              <p>
                You may not need an enterprise project management platform. But
                a notebook, scattered reminders, and an endless to-do list may
                no longer be enough.
              </p>

              <p className="landing-positioning__highlight">
                Postly lives between those two extremes.
              </p>
            </div>
          </div>
        </section>

        {/* =======================================================
            PRODUCTIVITY PHILOSOPHY
        ======================================================== */}

        <section className="landing-section">
          <div className="landing-container landing-section-grid">
            <div className="landing-section-grid__heading">
              <p className="landing-eyebrow">Our view of productivity</p>

              <h2>Productivity is not about doing more things.</h2>
            </div>

            <div className="landing-section-grid__content landing-prose">
              <p>
                It is easy to measure productivity by volume: more tasks
                completed, more messages answered, more boxes checked.
              </p>

              <p>
                Useful productivity is ultimately about directing attention
                toward work that matters and creating enough structure to finish
                it.
              </p>

              <p>
                That requires knowing what you are working toward. Knowing what
                comes next. Knowing what can wait. Remembering the context
                behind decisions. And building repeatable systems for
                responsibilities that should not depend entirely on memory.
              </p>

              <blockquote>
                The purpose of organization is not organization itself. The
                purpose is execution.
              </blockquote>
            </div>
          </div>
        </section>

        {/* =======================================================
            EVOLUTION
        ======================================================== */}

        <section className="landing-section landing-section--soft">
          <div className="landing-container">
            <div className="landing-section-heading landing-section-heading--center">
              <p className="landing-eyebrow">Designed to evolve</p>

              <h2>A system that can grow with the way you work.</h2>
            </div>

            <div className="landing-evolution">
              <div className="landing-evolution__item landing-evolution__item--yellow">
                <span>01</span>

                <p>An idea becomes a project.</p>
              </div>

              <div className="landing-evolution__item landing-evolution__item--blue">
                <span>02</span>

                <p>A project becomes a collection of tasks.</p>
              </div>

              <div className="landing-evolution__item landing-evolution__item--green">
                <span>03</span>

                <p>Repeated steps become a process.</p>
              </div>

              <div className="landing-evolution__item landing-evolution__item--yellow">
                <span>04</span>

                <p>A process becomes a reusable checklist.</p>
              </div>

              <div className="landing-evolution__item landing-evolution__item--blue">
                <span>05</span>

                <p>A recurring responsibility becomes a routine.</p>
              </div>
            </div>

            <div className="landing-evolution__closing">
              <p>
                What begins as personal organization can gradually become your
                own operating system for getting things done.
              </p>

              <p>
                Start simple. Add structure where structure creates value. Keep
                the system understandable. Keep moving.
              </p>
            </div>
          </div>
        </section>

        {/* =======================================================
            FINAL CTA
        ======================================================== */}

        <section className="landing-final-cta">
          <div className="landing-container landing-final-cta__inner">
            <p className="landing-eyebrow landing-eyebrow--light">
              Start with what matters
            </p>

            <h2>Build a better system for the work in front of you.</h2>

            <p>
              Organize projects, manage tasks, capture notes, establish
              priorities, build repeatable processes, create checklists, and
              maintain the routines that keep your work moving forward.
            </p>

            <p className="landing-final-cta__statement">
              Less clutter. More clarity. More execution.
            </p>

            <div className="landing-final-cta__actions">
              <Link
                className="landing-button landing-button--white"
                to="/auth/sign-up"
              >
                Create Your Free Account
              </Link>

              <a
                className="landing-button landing-button--dark-outline"
                href="/features"
              >
                Explore Features
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* =========================================================
          FOOTER
      ========================================================== */}

      <footer className="landing-footer">
        <div className="landing-container landing-footer__inner">
          <div className="landing-footer__intro">
            <a className="landing-footer__brand" href="/">
              Postly
            </a>

            <p>
              Task management and project planning for people who want a clearer
              way to get work done.
            </p>
          </div>

          <div className="landing-footer__links">
            <div>
              <p className="landing-footer__heading">Product</p>

              <a href="/features">Features</a>

              <a href="/pricing">Pricing</a>

              <a href="/use-cases">Use Cases</a>
            </div>

            <div>
              <p className="landing-footer__heading">Use Cases</p>

              <a href="/solo-founders">Solo Founders</a>

              <a href="/service-professionals">Service Professionals</a>

              <a href="/students">Students</a>

              <a href="/creators">Creators</a>
            </div>

            <div>
              <p className="landing-footer__heading">Company</p>

              <a href="/about">About</a>

              <Link to="/auth">Sign In</Link>

              <Link to="/auth/sign-up">Get Started</Link>
            </div>
          </div>
        </div>

        <div className="landing-container landing-footer__bottom">
          <p>© 2026 Roomatic. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
